/**
 * Project Context
 * Manages selected project state across the application
 * Replaces localStorage-based project selection to avoid race conditions and state inconsistencies
 */

import React, {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useState,
} from "react";

interface Project {
	id: string;
	name: string;
}

interface ProjectContextType {
	selectedProject: Project | null;
	setSelectedProject: (project: Project | null) => void;
	clearProject: () => void;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const useProject = () => {
	const context = useContext(ProjectContext);
	if (!context) {
		throw new Error("useProject must be used within a ProjectProvider");
	}
	return context;
};

interface ProjectProviderProps {
	children: ReactNode;
}

export const ProjectProvider: React.FC<ProjectProviderProps> = ({
	children,
}) => {
	const [selectedProject, setSelectedProjectState] = useState<Project | null>(
		null
	);

	const setSelectedProject = useCallback((project: Project | null) => {
		setSelectedProjectState(project);
	}, []);

	const clearProject = useCallback(() => {
		setSelectedProjectState(null);
	}, []);

	const value: ProjectContextType = {
		selectedProject,
		setSelectedProject,
		clearProject,
	};

	return (
		<ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>
	);
};
